import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import './EmailConfigurations.css';
import useSortableTable from '../common/useSortableTable';
import { usePermissions } from '../../services/PermissionsContext'; // 1. Import

const API_URL = '/api/masters/banks/';

const getAuthHeaders = (isFormData = false) => {
    const headers = {
        'Authorization': `Bearer ${localStorage.getItem('kk_token')}`
    };
    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
    }
    return headers;
};

const Banks = () => {
    const { hasPermission } = usePermissions(); // 2. Get the permission checker function

    // State for bank list and CRUD
    const [banks, setBanks] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentBank, setCurrentBank] = useState(null); // For editing or adding

    // State for CSV upload
    const [selectedFile, setSelectedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const { items: sortedBanks, requestSort, getSortIndicator } = useSortableTable(banks);

    const fetchBanks = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await fetch(API_URL, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error('Failed to fetch banks.');
            const data = await response.json();
            setBanks(data);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        // 3. Check for view permission before fetching data
        if (hasPermission('BANKS', 'can_view')) {
            fetchBanks();
        }
    }, [fetchBanks, hasPermission]);

    // --- Modal and Form Handlers ---
    const handleOpenModal = (bank = null) => {
        // 4. Permission check for opening the modal
        const canOpen = bank ? hasPermission('BANKS', 'can_edit') : hasPermission('BANKS', 'can_create');
        if (!canOpen) {
            toast.error("You don't have permission to perform this action.");
            return;
        }
        setCurrentBank(bank ? { ...bank } : { name: '', swift_code: '' });
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setCurrentBank(null);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setCurrentBank(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        if (!currentBank || !currentBank.name) {
            toast.warn('Bank Name is required.');
            return;
        }

        const isEditing = !!currentBank.id;
        // 5. Permission check before saving
        const canSave = isEditing ? hasPermission('BANKS', 'can_edit') : hasPermission('BANKS', 'can_create');
        if (!canSave) {
            toast.error("You don't have permission to save.");
            return;
        }

        const url = isEditing ? `${API_URL}${currentBank.id}/` : API_URL;
        const method = isEditing ? 'PUT' : 'POST';

        try {
            const response = await fetch(url, {
                method,
                headers: getAuthHeaders(),
                body: JSON.stringify(currentBank),
            });

            const result = await response.json();
            if (!response.ok) {
                // Display specific duplicate errors from backend
                const errorMsg = Object.values(result).flat().join(' ');
                throw new Error(errorMsg || 'Failed to save bank.');
            }

            toast.success(`Bank ${isEditing ? 'updated' : 'added'} successfully!`);
            handleCloseModal();
            fetchBanks(); // Refresh the list
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleDelete = async (bankId) => {
        // 6. Permission check before deleting
        if (!hasPermission('BANKS', 'can_delete')) {
            toast.error("You don't have permission to delete banks.");
            return;
        }
        if (!window.confirm('Are you sure you want to delete this bank?')) return;

        try {
            const response = await fetch(`${API_URL}${bankId}/`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
            });

            if (!response.ok) throw new Error('Failed to delete bank.');

            toast.success('Bank deleted successfully!');
            fetchBanks(); // Refresh the list
        } catch (error) {
            toast.error(error.message);
        }
    };

    // --- CSV Upload Handlers ---
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type === 'text/csv') {
            setSelectedFile(file);
        } else {
            toast.error('Please select a valid .csv file.');
            setSelectedFile(null);
            e.target.value = null;
        }
    };

    const handleUpload = async () => {
        // 7. Permission check before uploading
        if (!hasPermission('BANKS', 'can_create')) {
            toast.error("You don't have permission to upload banks.");
            return;
        }
        if (!selectedFile) {
            toast.warn('Please select a file to upload.');
            return;
        }
        setIsUploading(true);
        const formData = new FormData();
        formData.append('file', selectedFile);

        try {
            const response = await fetch(`${API_URL}upload-csv/`, {
                method: 'POST',
                headers: getAuthHeaders(true),
                body: formData,
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.error || 'Failed to upload CSV.');

            toast.success(
                `CSV processed! Created: ${result.created}, Updated: ${result.updated}`
            );
            setSelectedFile(null);
            document.getElementById('bank-csv-input').value = null;
            fetchBanks(); // Refresh the list
        } catch (error) {
            toast.error(`Upload failed: ${error.message}`);
        } finally {
            setIsUploading(false);
        }
    };

    // 8. Top-level permission check for viewing the page
    if (!hasPermission('BANKS', 'can_view')) {
        return (
            <div>
                <h3>Permission Denied</h3>
                <p>You do not have permission to view this page.</p>
            </div>
        );
    }

    return (
        <div>
            {/* 9. Conditionally render CSV Upload Section */}
            {hasPermission('BANKS', 'can_create') && (
                <div className="admin-utility-section">
                    <h3>Upload Banks from CSV</h3>
                    <p className="instructions">
                        Upload a CSV file with headers: <strong>name</strong> and <strong>swift_code</strong>.
                    </p>
                    <div className="upload-controls">
                        <input id="bank-csv-input" type="file" accept=".csv" onChange={handleFileChange} disabled={isUploading} />
                        <button onClick={handleUpload} disabled={!selectedFile || isUploading} className="upload-btn">
                            {isUploading ? 'Uploading...' : 'Upload CSV'}
                        </button>
                    </div>
                </div>
            )}

            {/* Bank Management Table */}
            <div className="admin-utility-section" style={{ marginTop: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3>Manage Banks</h3>
                    {/* 10. Conditionally render "Add New Bank" button */}
                    {hasPermission('BANKS', 'can_create') && (
                        <button onClick={() => handleOpenModal()} className="add-btn">Add New Bank</button>
                    )}
                </div>
                <div className="table-container">
                    <table className="jobs-table">
                        <thead>
                            <tr>
                                <th onClick={() => requestSort('name')} style={{ cursor: 'pointer' }}>Bank Name {getSortIndicator('name')}</th>
                                <th onClick={() => requestSort('swift_code')} style={{ cursor: 'pointer' }}>Swift Code {getSortIndicator('swift_code')}</th>
                                {/* 11. Conditionally render Actions column header */}
                                {(hasPermission('BANKS', 'can_edit') || hasPermission('BANKS', 'can_delete')) && (
                                    <th>Actions</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr><td colSpan="3">Loading...</td></tr>
                            ) : banks.length > 0 ? (
                                sortedBanks.map(bank => (
                                    <tr key={bank.id}>
                                        <td>{bank.name}</td>
                                        <td>{bank.swift_code || 'N/A'}</td>
                                        {/* 12. Conditionally render the actions cell and buttons */}
                                        {(hasPermission('BANKS', 'can_edit') || hasPermission('BANKS', 'can_delete')) && (
                                            <td>
                                                {hasPermission('BANKS', 'can_edit') && (
                                                    <button className="icon-btn edit-btn" onClick={() => handleOpenModal(bank)} title="Edit">
                                                        <i className="fa fa-pencil-alt"></i>
                                                    </button>
                                                )}
                                                {hasPermission('BANKS', 'can_delete') && (
                                                    <button className="icon-btn cancel-btn" onClick={() => handleDelete(bank.id)} title="Delete">
                                                        <i className="fa fa-trash"></i>
                                                    </button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="3">No banks found.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add/Edit Modal */}
            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '500px' }}>
                        <h4>{currentBank.id ? 'Edit Bank' : 'Add New Bank'}</h4>
                        <div className="form-group">
                            <label>Bank Name</label>
                            <input type="text" name="name" value={currentBank.name} onChange={handleFormChange} className="form-control" />
                        </div>
                        <div className="form-group">
                            <label>Swift Code</label>
                            <input type="text" name="swift_code" value={currentBank.swift_code} onChange={handleFormChange} className="form-control" />
                        </div>
                        <div className="modal-actions">
                            <button onClick={handleSave} className="add-btn">Save</button>
                            <button onClick={handleCloseModal} className="cancel-btn">Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Banks;